/**
 * RiskMLDashboard — Risk alert dashboard
 *
 * Flat modern design, single-page layout.
 * Hero metrics strip → Trend chart → All stocks table (grouped by severity) → Re-entry table.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Alert, CircularProgress, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, TextField, InputAdornment, Select, MenuItem, FormControl,
  Button, Collapse, ToggleButton, ToggleButtonGroup, Skeleton,
} from '@mui/material';
import {
  ShieldOutlined as ShieldIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  TrendingFlat as TrendFlatIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Public as PublicIcon,
  NotificationsActive as AlertBellIcon,
  Timeline as TimelineIcon,
  Login as ReEntryIcon,
  Calculate as CalculateIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import {
  Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
  ComposedChart, Line,
} from 'recharts';
import { riskMLAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════ */
const SEV = {
  0: { key: 'safe',     bg: '#22c55e', light: '#f0fdf4', text: '#15803d' },
  1: { key: 'caution',  bg: '#eab308', light: '#fefce8', text: '#a16207' },
  2: { key: 'warning',  bg: '#ef4444', light: '#fef2f2', text: '#b91c1c' },
  3: { key: 'critical', bg: '#a855f7', light: '#faf5ff', text: '#7e22ce' },
};

const REGIME_CLR = v => v >= 0.40 ? '#dc2626' : v >= 0.15 ? '#ea580c' : '#22c55e';

/* ── Collapsible Section ────────────────────────────────── */
const Section = ({ title, icon, children, defaultOpen = true, tooltip, contentSx, action }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper elevation={0} sx={{
      borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
    }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, cursor: 'pointer',
          background: open ? 'linear-gradient(135deg, #fafafa 0%, #fff 100%)' : '#fafafa',
          borderBottom: open ? '1px solid #f3f4f6' : 'none',
          transition: 'all 0.2s',
          '&:hover': { background: '#f9fafb' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && <Box sx={{ color: '#9ca3af', display: 'flex' }}>{icon}</Box>}
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <InfoIcon sx={{ fontSize: 14, color: '#d1d5db', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action && <Box onClick={e => e.stopPropagation()}>{action}</Box>}
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: '#f3f4f6', transition: 'transform 0.3s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            <ExpandMoreIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
          </Box>
        </Box>
      </Box>
      <Collapse in={open} timeout={300}>
        <Box sx={{ p: 2.5, ...contentSx }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const RiskDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [, setSeverity] = useState([]);
  const [trend, setTrend] = useState([]);
  const [modelInfo, setModelInfo] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [searchTicker, setSearchTicker] = useState('');
  const [safeExpanded, setSafeExpanded] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('risky');

  /* ─── Fetch ─────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dp = selectedDate || undefined;
      const [dashR, sevR, trendR, modelR, heatR, datesR] = await Promise.allSettled([
        riskMLAPI.getDashboard(dp), riskMLAPI.getSeverity(dp), riskMLAPI.getTrend(30),
        riskMLAPI.getModel(), riskMLAPI.getHeatmap(dp), riskMLAPI.getDates(60),
      ]);
      if (dashR.status === 'fulfilled') setDashboard(dashR.value.data);
      if (sevR.status === 'fulfilled') setSeverity(sevR.value.data);
      if (trendR.status === 'fulfilled') setTrend(trendR.value.data?.reverse?.() || trendR.value.data || []);
      if (modelR.status === 'fulfilled') setModelInfo(modelR.value.data);
      if (heatR.status === 'fulfilled') setHeatmapData(heatR.value.data || []);
      if (datesR.status === 'fulfilled') setAvailableDates(datesR.value.data || []);
      if (dashR.status === 'rejected') setError(dashR.reason?.response?.data?.detail || t('riskML.failedToLoad'));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [selectedDate, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Recalculate ────────────────────────────────────── */
  const handleRecalculate = useCallback(async () => {
    setCalculating(true);
    try {
      const res = await riskMLAPI.calculate();
      const d = res.data;
      setSnackbar({ open: true, severity: 'success',
        message: t('riskML.recalculate.success', { n: d.n_saved, alerts: d.n_alerts, seconds: d.elapsed_seconds }) });
      await fetchData();
    } catch (e) {
      setSnackbar({ open: true, severity: 'error',
        message: t('riskML.recalculate.error', { detail: e.response?.data?.detail || e.message }) });
    } finally { setCalculating(false); }
  }, [fetchData, t]);

  /* ─── Derived ────────────────────────────────────────── */
  const summary   = dashboard?.summary;
  const topRisky  = dashboard?.top_risky || [];
  const reEntries = dashboard?.re_entries || [];
  const regimeVal = summary?.mkt_regime_risk_ema || 0;
  const regimeClr = REGIME_CLR(regimeVal);
  const regimeLbl = regimeVal >= .40 ? t('riskML.regime.highRisk') : regimeVal >= .15 ? t('riskML.regime.moderate') : t('riskML.regime.calm');

  const filtered = useMemo(() => heatmapData.filter(d => !search || d.ticker.toLowerCase().includes(search.toLowerCase())), [heatmapData, search]);
  const groups   = useMemo(() => [3,2,1,0].map(tier => ({ tier, items: filtered.filter(d => d.severity_tier === tier) })), [filtered]);
  const trendData = useMemo(() => trend.map(r => ({ ...r, avgPct: +(r.avg_prob*100).toFixed(1), regPct: +((r.mkt_regime||0)*100).toFixed(1) })), [trend]);

  /* ─── Stale data ──────────────────────────────────────── */
  const dataDate = summary?.date ? new Date(summary.date) : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysDiff = dataDate ? Math.floor((today - dataDate) / (1000 * 60 * 60 * 24)) : 0;
  const isStale = daysDiff > 3;

  /* ─── Day-over-day deltas ─────────────────────────────── */
  const prevTrend = trend.length >= 2 ? trend[trend.length - 2] : null;
  const currTrend = trend.length >= 1 ? trend[trend.length - 1] : null;
  const alertsDelta = prevTrend && currTrend
    ? ((currTrend.n_critical||0) + (currTrend.n_warning||0)) - ((prevTrend.n_critical||0) + (prevTrend.n_warning||0)) : null;
  const riskDelta = prevTrend && currTrend
    ? ((currTrend.avg_prob||0) - (prevTrend.avg_prob||0)) * 100 : null;

  /* ─── Risk Outlook ────────────────────────────────────── */
  const riskOutlook = useMemo(() => {
    if (trend.length < 5) return null;
    const w = Math.min(trend.length, 7);
    const recent = trend.slice(-w);
    const n = recent.length;
    const xm = (n - 1) / 2;
    const y = recent.map(r => (r.avg_prob || 0) * 100);
    const ym = y.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) { num += (i - xm) * (y[i] - ym); den += (i - xm) ** 2; }
    const slope = den ? num / den : 0;
    const yA = recent.map(r => (r.n_critical || 0) + (r.n_warning || 0));
    const yAm = yA.reduce((a, b) => a + b, 0) / n;
    let numA = 0;
    for (let i = 0; i < n; i++) numA += (i - xm) * (yA[i] - yAm);
    const aSlope = den ? numA / den : 0;
    const last = recent[n - 1];
    const curR = +(((last.avg_prob || 0) * 100).toFixed(1));
    const curA = (last.n_critical || 0) + (last.n_warning || 0);
    const fd = 3;
    const projR = +Math.max(0, Math.min(100, curR + slope * fd)).toFixed(1);
    const projA = Math.max(0, Math.round(curA + aSlope * fd));
    const dir = slope > 1.5 ? 'risingFast' : slope > 0.3 ? 'rising'
      : slope > -0.3 ? 'stable' : slope > -1.5 ? 'falling' : 'fallingFast';
    return { slope: +slope.toFixed(2), currentRisk: curR, projectedRisk: projR,
      riskDelta: +(projR - curR).toFixed(1), currentAlerts: curA, projectedAlerts: projA,
      direction: dir, forecastDays: fd };
  }, [trend]);

  /* ─── Chart data with projection ─────────────────────── */
  const chartData = useMemo(() => {
    if (!riskOutlook || trendData.length < 3) return trendData;
    const data = trendData.map((d, i) =>
      i === trendData.length - 1 ? { ...d, projAvgPct: d.avgPct } : d);
    const lastDate = trend[trend.length - 1]?.date;
    if (!lastDate) return data;
    for (let i = 1; i <= 3; i++) {
      const d = new Date(lastDate);
      d.setDate(d.getDate() + i);
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      data.push({ date: d.toISOString().slice(0, 10),
        projAvgPct: +Math.max(0, Math.min(100, riskOutlook.currentRisk + riskOutlook.slope * i)).toFixed(1),
        isProjection: true });
    }
    return data;
  }, [trendData, riskOutlook, trend]);

  /* ─── Helpers ─────────────────────────────────────────── */
  const sevLabel = tier => t(`riskML.severity.${(SEV[tier]||SEV[0]).key}`);
  const getAction = (row) => {
    const tier = row.severity_tier;
    if (tier >= 3) return row.target_position < 0.3 ? 'sellAvoid' : 'reduce';
    if (tier >= 2) return row.target_position < 0.5 ? 'reduce' : 'monitor';
    if (tier >= 1) return 'watch';
    if (row.re_entry_signal) return 'buySignal';
    return 'hold';
  };
  const actionClr = k => k === 'sellAvoid' ? '#dc2626' : k === 'reduce' ? '#ea580c'
    : k === 'buySignal' ? '#16a34a' : '#6b7280';

  /* ═══════════════════════════════════════════════════════════
     LOADING / ERROR
     ═══════════════════════════════════════════════════════════ */
  if (loading) {
    return (
      <Box sx={{ minHeight: '80vh' }}>
        {/* Header skeleton */}
        <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2 }} />
            <Box>
              <Skeleton variant="text" width={200} height={26} />
              <Skeleton variant="text" width={280} height={14} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </Box>

        <Box sx={{ px: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Hero metrics strip skeleton */}
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', '& > *': { flex: '1 1 0', px: 2.5, py: 2, borderRight: '1px solid #f3f4f6' }, '& > *:last-child': { borderRight: 'none' } }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Box key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                    <Skeleton variant="circular" width={14} height={14} />
                    <Skeleton variant="text" width={80} height={14} />
                  </Box>
                  <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 1, mb: 0.5 }} />
                  <Skeleton variant="text" width={100} height={12} />
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Trend chart skeleton */}
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width={140} height={18} />
            </Box>
            <Box sx={{ px: 2, py: 2 }}>
              <Skeleton variant="rounded" width="100%" height={180} sx={{ borderRadius: 1 }} />
            </Box>
          </Paper>

          {/* Table skeleton */}
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="text" width={160} height={18} />
              </Box>
              <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 1 }} />
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              {/* Table header */}
              <Box sx={{ display: 'flex', gap: 2, py: 1, borderBottom: '1px solid #f3f4f6' }}>
                {[60, 100, 70, 70, 80, 80, 70, 60].map((w, i) => (
                  <Skeleton key={i} variant="text" width={w} height={14} />
                ))}
              </Box>
              {/* Table rows */}
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <Box key={i} sx={{ display: 'flex', gap: 2, py: 1.2, borderBottom: '1px solid #fafafa' }}>
                  {[60, 100, 70, 70, 80, 80, 70, 60].map((w, j) => (
                    <Skeleton key={j} variant="text" width={w} height={14} />
                  ))}
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Re-entry table skeleton */}
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width={180} height={18} />
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              {[1, 2, 3, 4].map(i => (
                <Box key={i} sx={{ display: 'flex', gap: 2, py: 1.2, borderBottom: '1px solid #fafafa' }}>
                  {[60, 100, 70, 80, 80, 70].map((w, j) => (
                    <Skeleton key={j} variant="text" width={w} height={14} />
                  ))}
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}
          action={<Chip label={t('riskML.retry')} onClick={fetchData} size="small"
            sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 600 }} />}
        >{error}</Alert>
      </Box>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #ef4444, #a855f7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
          }}>
            <ShieldIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('riskML.pageTitle')}
              </Typography>
              <Chip label={regimeLbl} size="small" sx={{
                height: 20, fontSize: 10, fontWeight: 800, bgcolor: regimeClr, color: '#fff',
              }} />
              {riskOutlook && riskOutlook.direction !== 'stable' && (
                <Chip
                  icon={riskOutlook.direction.includes('rising')
                    ? <TrendUpIcon sx={{ fontSize: 13 }} />
                    : <TrendDownIcon sx={{ fontSize: 13 }} />}
                  label={t(`riskML.outlook.${riskOutlook.direction}`)}
                  size="small" variant="outlined"
                  sx={{
                    height: 20, fontSize: 10, fontWeight: 700,
                    borderColor: riskOutlook.direction.includes('rising') ? '#ef4444' : '#22c55e',
                    color: riskOutlook.direction.includes('rising') ? '#ef4444' : '#22c55e',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
              {modelInfo && (
                <Tooltip arrow placement="bottom" title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5 }}>{t('riskML.model.title', { version: modelInfo.version })}</Typography>
                    <Typography sx={{ fontSize: 11 }}>{modelInfo.model_type || t('riskML.model.type')}</Typography>
                    <Typography sx={{ fontSize: 11 }}>{t('riskML.model.modelsAndFeatures', { nModels: modelInfo.n_models, featureCount: modelInfo.feature_count })}</Typography>
                    <Typography sx={{ fontSize: 11 }}>{t('riskML.model.samples', { samples: modelInfo.train_samples?.toLocaleString(), posRate: (modelInfo.train_pos_rate*100).toFixed(1) })}</Typography>
                  </Box>
                }>
                  <Chip size="small" variant="outlined"
                    icon={<InfoIcon sx={{ fontSize: 12 }} />}
                    label={t('riskML.model.chipLabel', { version: modelInfo.version, nModels: modelInfo.n_models })}
                    sx={{ height: 20, fontSize: 10, fontWeight: 600, borderColor: '#d1d5db', color: '#6b7280' }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('riskML.pageDesc')}
              {summary?.date && ` \u00B7 ${summary.date}`}
              {summary?.model_version && ` \u00B7 ${summary.model_version}`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder={t('riskML.goToTicker')}
            value={searchTicker}
            onChange={e => setSearchTicker(e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === 'Enter' && searchTicker.trim()) { navigate(`/risk-detail/${searchTicker.trim()}`); setSearchTicker(''); } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1, height: 32, fontSize: 13 } }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={selectedDate} onChange={e => setSelectedDate(e.target.value)} displayEmpty
              sx={{ borderRadius: 1, height: 32, fontSize: 13 }}>
              <MenuItem value=""><em>{t('riskML.latestDate')}</em></MenuItem>
              {availableDates.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={!calculating ? handleRecalculate : undefined}
            disabled={calculating}
            startIcon={calculating ? <CircularProgress size={14} /> : <CalculateIcon sx={{ fontSize: 16, mr: 0.5 }} />}
            sx={{
              fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#ef4444', textTransform: 'none',
              '&:hover': { bgcolor: '#dc2626' },
            }}
          >
            {calculating ? t('riskML.recalculate.running') : t('riskML.recalculate.button')}
          </Button>
          <Button
            variant="outlined"
            onClick={fetchData}
            startIcon={<RefreshIcon sx={{ fontSize: 16, mr: 0.5 }} />}
            sx={{
              fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0', textTransform: 'none',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
            }}
          >
            {t('common.refresh', 'Làm mới')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ═══════════════════════════════════════════════════════
            BANNERS
            ═══════════════════════════════════════════════════════ */}
        {calculating && (
          <Alert severity="info" icon={<CircularProgress size={16} />}
            sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: 13, fontWeight: 600 } }}>
            {t('riskML.recalculate.banner')}
          </Alert>
        )}
        {summary && isStale && (
          <Alert severity="info" variant="outlined"
            sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: 13, fontWeight: 500 } }}>
            {t('riskML.staleDataWarning', { date: summary.date, days: daysDiff })}
          </Alert>
        )}
        {summary && regimeVal >= 0.15 && (
          <Alert severity={regimeVal >= .40 ? 'error' : 'warning'} variant="filled"
            sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: 13, fontWeight: 500 } }}>
            {regimeVal >= .40
              ? t('riskML.marketBanner.highRisk', { critical: summary?.n_critical || 0 })
              : t('riskML.marketBanner.moderate')}
          </Alert>
        )}

        {/* ═══════════════════════════════════════════════════════
            HERO METRICS
            ═══════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{
          borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden',
          background: 'linear-gradient(135deg, #fafafa 0%, #fff 60%)',
        }}>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap',
            '& > *': { flex: '1 1 0', minWidth: 130, px: 2.5, py: 2, borderRight: '1px solid #f3f4f6' },
            '& > *:last-child': { borderRight: 'none' },
          }}>
            {/* Market Regime */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <PublicIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.statCards.marketRegime')}
                </Typography>
              </Box>
              <Chip label={regimeLbl} size="small" sx={{
                height: 24, fontSize: 11, fontWeight: 800, bgcolor: regimeClr, color: '#fff',
              }} />
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                {t('riskML.statCards.regimeSub', { regime: (regimeVal*100).toFixed(1), stress: ((summary?.deriv_stress_ema||0)*100).toFixed(1) })}
              </Typography>
            </Box>

            {/* Active Alerts */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <AlertBellIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.statCards.activeAlerts')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: (summary?.n_alerts || 0) > 0 ? '#ef4444' : '#22c55e', lineHeight: 1 }}>
                  {summary?.n_alerts || 0}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>
                  / {summary?.n_tickers || 0}
                </Typography>
              </Box>
              {alertsDelta !== null && alertsDelta !== 0 && (
                <Typography sx={{ fontSize: 11, color: alertsDelta > 0 ? '#ef4444' : '#22c55e', fontWeight: 600, mt: 0.3 }}>
                  {alertsDelta > 0 ? '\u25B2' : '\u25BC'} {alertsDelta > 0 ? '+' : ''}{alertsDelta} {t('riskML.delta.vsYesterday')}
                </Typography>
              )}
            </Box>

            {/* Avg Risk */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <TimelineIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.statCards.averageRisk')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {((summary?.avg_prob||0)*100).toFixed(1)}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>%</Typography>
              </Box>
              {riskDelta !== null && Math.abs(riskDelta) >= 0.1 && (
                <Typography sx={{ fontSize: 11, color: riskDelta > 0 ? '#ef4444' : '#22c55e', fontWeight: 600, mt: 0.3 }}>
                  {riskDelta > 0 ? '\u25B2' : '\u25BC'} {riskDelta > 0 ? '+' : ''}{riskDelta.toFixed(1)}% {t('riskML.delta.vsYesterday')}
                </Typography>
              )}
            </Box>

            {/* Severity Breakdown */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <WarningIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.sections.severityDistribution')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                {[3, 2, 1, 0].map(tier => {
                  const s = SEV[tier];
                  const count = tier === 0 ? (summary?.n_safe || 0)
                    : tier === 1 ? (summary?.n_caution || 0)
                    : tier === 2 ? (summary?.n_warning || 0)
                    : (summary?.n_critical || 0);
                  return count > 0 ? (
                    <Chip key={tier} size="small"
                      label={`${sevLabel(tier)}: ${count}`}
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: `${s.bg}1A`, color: s.text,
                        border: `1px solid ${s.bg}33`,
                      }}
                    />
                  ) : null;
                })}
              </Box>
            </Box>

            {/* Re-Entry */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <ReEntryIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.statCards.reEntrySignals')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#22c55e', lineHeight: 1 }}>
                  {reEntries.length}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.3 }}>
                {t('riskML.statCards.reEntrySub')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* ═══════════════════════════════════════════════════════
            RISK FORECAST STRIP
            ═══════════════════════════════════════════════════════ */}
        {riskOutlook && (
          <Paper elevation={0} sx={{
            borderRadius: 1.5, border: '1px solid #e5e7eb', px: 2.5, py: 1.5,
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            bgcolor: riskOutlook.direction.includes('rising') ? '#fef2f2' : riskOutlook.direction.includes('falling') ? '#f0fdf4' : '#fafafa',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              {riskOutlook.direction.includes('rising')
                ? <TrendUpIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                : riskOutlook.direction.includes('falling')
                ? <TrendDownIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                : <TrendFlatIcon sx={{ fontSize: 18, color: '#ea580c' }} />}
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1e293b' }}>
                {t('riskML.outlook.title')}
              </Typography>
              <Chip label={t(`riskML.outlook.${riskOutlook.direction}`)} size="small"
                sx={{
                  height: 20, fontSize: 10, fontWeight: 700,
                  bgcolor: riskOutlook.direction.includes('rising') ? '#ef44441A'
                    : riskOutlook.direction.includes('falling') ? '#22c55e1A' : '#ea580c1A',
                  color: riskOutlook.direction.includes('rising') ? '#ef4444'
                    : riskOutlook.direction.includes('falling') ? '#22c55e' : '#ea580c',
                }} />
            </Box>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              {t('riskML.outlook.avgRisk')}: <Box component="span" sx={{ fontWeight: 700, color: '#2563eb' }}>{riskOutlook.currentRisk}% &rarr; {riskOutlook.projectedRisk}%</Box>
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              {t('riskML.outlook.alerts')}: <Box component="span" sx={{ fontWeight: 700, color: '#dc2626' }}>{riskOutlook.currentAlerts} &rarr; ~{riskOutlook.projectedAlerts}</Box>
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', flex: 1 }}>
              {t(`riskML.outlook.guidance.${riskOutlook.direction}`)}
            </Typography>
          </Paper>
        )}

        {/* ═══════════════════════════════════════════════════════
            TREND CHART
            ═══════════════════════════════════════════════════════ */}
        <Section
          title={t('riskML.sections.riskAlertTrend')}
          icon={<ChartIcon sx={{ fontSize: 16 }} />}
        >
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="gcrit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gwarn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false}
                  tickFormatter={d => d?.slice(5)} />
                <YAxis yAxisId="L" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="R" orientation="right" domain={[0, 100]} fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} hide />
                <RTooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{label}</Typography>
                      {payload.map((p, i) => (
                        <Typography key={i} sx={{ color: p.color, fontSize: 11 }}>
                          {p.name}: {p.value}{p.unit||''}
                        </Typography>
                      ))}
                    </Paper>
                  );
                }} />
                <Area yAxisId="L" type="monotone" dataKey="n_critical" name={t('riskML.chart.critical')} stackId="1" stroke="#a855f7" fill="url(#gcrit)" />
                <Area yAxisId="L" type="monotone" dataKey="n_warning"  name={t('riskML.chart.warning')}  stackId="1" stroke="#ef4444" fill="url(#gwarn)" />
                <Line yAxisId="R" type="monotone" dataKey="avgPct"  name={t('riskML.chart.avgRiskPct')}  stroke="#2563eb" strokeWidth={2} dot={false} unit="%" />
                <Line yAxisId="R" type="monotone" dataKey="regPct"  name={t('riskML.chart.regimePct')}    stroke="#ea580c" strokeWidth={1.5} dot={false} strokeDasharray="4 2" unit="%" />
                <Line yAxisId="R" type="monotone" dataKey="projAvgPct" name={t('riskML.chart.projected')} stroke="#2563eb" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: '#2563eb' }} unit="%" connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <Typography sx={{ color: '#9ca3af', fontSize: 13 }}>{t('riskML.noTrendData')}</Typography>
            </Box>
          )}
        </Section>

        {/* ═══════════════════════════════════════════════════════
            STOCKS TABLE
            ═══════════════════════════════════════════════════════ */}
        <Section
          title={viewMode === 'risky' ? t('riskML.sections.top20Title') : t('riskML.tabs.heatmap', { count: heatmapData.length })}
          icon={<WarningIcon sx={{ fontSize: 16 }} />}
          contentSx={{ p: 0 }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {viewMode === 'all' && (
                <TextField size="small" placeholder={t('riskML.filter')} value={search} onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14 }} /></InputAdornment> }}
                  sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 28, fontSize: 11 } }} />
              )}
              <ToggleButtonGroup
                value={viewMode} exclusive
                onChange={(_, v) => v && setViewMode(v)}
                size="small"
                sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.3, fontSize: 11, fontWeight: 600, textTransform: 'none' } }}
              >
                <ToggleButton value="risky">Top 20</ToggleButton>
                <ToggleButton value="all">{t('riskML.dashboard.allStocks')} ({heatmapData.length})</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          }
        >
          {viewMode === 'risky' ? (
            /* ── Top 20 Risky ── */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    background: 'linear-gradient(180deg, #fef2f2 0%, #fff1f2 100%)',
                    '& th': { fontWeight: 700, fontSize: 11, color: '#b91c1c', py: 1,
                      borderBottom: '2px solid #fecaca', whiteSpace: 'nowrap', bgcolor: '#fef2f2' },
                  }}>
                    <TableCell sx={{ width: 36 }}>{t('riskML.tableHeaders.rank')}</TableCell>
                    <TableCell>{t('riskML.tableHeaders.ticker')}</TableCell>
                    <TableCell align="right">{t('riskML.tableHeaders.riskProb')}</TableCell>
                    <TableCell align="center">{t('riskML.tableHeaders.severity')}</TableCell>
                    <TableCell align="right">{t('riskML.tableHeaders.positionSize')}</TableCell>
                    <TableCell align="center">{t('riskML.tableHeaders.signal')}</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('riskML.tableHeaders.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topRisky.map((s, idx) => {
                    const sev = SEV[s.severity_tier] || SEV[0];
                    const ak = getAction(s);
                    return (
                      <TableRow key={s.ticker} hover onClick={() => navigate(`/risk-detail/${s.ticker}`)}
                        sx={{
                          cursor: 'pointer',
                          background: s.severity_tier >= 2
                            ? `linear-gradient(90deg, ${sev.light} 0%, #fff 30%)`
                            : 'transparent',
                          '& td': { borderBottom: '1px solid #f3f4f6', py: 1 },
                          '& td:first-of-type': { borderLeft: `3px solid ${sev.bg}` },
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background 0.15s',
                        }}>
                        <TableCell>
                          <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{idx + 1}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, color: s.severity_tier >= 2 ? sev.text : '#1e293b' }}>
                            {s.ticker}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, justifyContent: 'flex-end' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: sev.bg }}>
                              {(s.ml_prob*100).toFixed(1)}%
                            </Typography>
                            <Box sx={{ width: 50, height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: '#f3f4f6' }}>
                              <Box sx={{ height: '100%', borderRadius: 2, width: `${Math.min(s.ml_prob*100, 100)}%`, bgcolor: sev.bg }} />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={s.severity_label} size="small" sx={{
                            height: 18, fontSize: 10, fontWeight: 800, bgcolor: sev.bg, color: '#fff',
                            '& .MuiChip-label': { px: 0.6 },
                          }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{
                            fontWeight: 600, fontSize: 12,
                            color: s.target_position < 0.3 ? '#ef4444' : s.target_position < 0.7 ? '#ea580c' : '#22c55e',
                          }}>
                            {(s.target_position*100).toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {s.dynamic_alert && (
                              <Chip label={t('riskML.signals.alert')} size="small" sx={{
                                height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#ef4444', color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }} />
                            )}
                            {s.re_entry_signal && (
                              <Chip label={t('riskML.signals.reEntry')} size="small" sx={{
                                height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#22c55e', color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }} />
                            )}
                            {!s.dynamic_alert && !s.re_entry_signal && (
                              <Typography sx={{ fontSize: 11, color: '#d1d5db' }}>&mdash;</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: actionClr(ak) }}>
                            {t(`riskML.actions.${ak}`)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* ── All Stocks (grouped by severity) ── */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    background: 'linear-gradient(180deg, #fafafa 0%, #fff 100%)',
                    '& th': { fontWeight: 700, fontSize: 11, color: '#6b7280', py: 1,
                      borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap', bgcolor: '#fafafa' },
                  }}>
                    <TableCell sx={{ pl: 2 }}>{t('riskML.tableHeaders.ticker')}</TableCell>
                    <TableCell>{t('riskML.tableHeaders.severity')}</TableCell>
                    <TableCell>
                      <Tooltip title={t('riskML.tooltips.riskPct')} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
                          {t('riskML.tableHeaders.risk')} <InfoIcon sx={{ fontSize: 11, color: '#d1d5db' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('riskML.tooltips.position')} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
                          {t('riskML.tableHeaders.position')} <InfoIcon sx={{ fontSize: 11, color: '#d1d5db' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title={t('riskML.tooltips.confidence')} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
                          {t('riskML.tableHeaders.confidence')} <InfoIcon sx={{ fontSize: 11, color: '#d1d5db' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('riskML.tableHeaders.signal')}</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('riskML.tableHeaders.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map(g => {
                    if (!g.items.length) return null;
                    const sev = SEV[g.tier];
                    const desc = g.tier === 3 ? t('riskML.tiers.criticalDesc')
                      : g.tier === 2 ? t('riskML.tiers.warningDesc')
                      : g.tier === 1 ? t('riskML.tiers.cautionDesc')
                      : t('riskML.tiers.safeDesc');
                    const items = g.tier === 0 && !safeExpanded ? g.items.slice(0, 25) : g.items;
                    return (
                      <React.Fragment key={g.tier}>
                        {/* Group header */}
                        <TableRow sx={{ '& td': { py: 0.8, border: 0 } }}>
                          <TableCell colSpan={7} sx={{ pl: 1.5, bgcolor: `${sev.bg}0D` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sev.bg }} />
                              <Typography sx={{ fontWeight: 800, fontSize: 12, color: sev.text }}>{sevLabel(g.tier)}</Typography>
                              <Chip label={g.items.length} size="small"
                                sx={{ height: 18, minWidth: 24, fontSize: 10, fontWeight: 700, borderRadius: 1,
                                  bgcolor: `${sev.bg}1A`, color: sev.text }} />
                              <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 0.5 }}>{desc}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {/* Data rows */}
                        {items.map(row => {
                          const s = SEV[row.severity_tier] || SEV[0];
                          const unc = (row.ensemble_std || 0) * 100;
                          const conf = unc < 3 ? t('riskML.confidenceLevels.high') : unc < 8 ? t('riskML.confidenceLevels.med') : t('riskML.confidenceLevels.low');
                          const confClr = unc < 3 ? '#22c55e' : unc < 8 ? '#ea580c' : '#ef4444';
                          const ak = getAction(row);
                          return (
                            <TableRow key={row.ticker} hover onClick={() => navigate(`/risk-detail/${row.ticker}`)}
                              sx={{
                                cursor: 'pointer',
                                '& td:first-of-type': { borderLeft: `3px solid ${s.bg}` },
                                background: row.severity_tier >= 2 ? `linear-gradient(90deg, ${s.light} 0%, #fff 30%)` : 'transparent',
                                '&:hover': { bgcolor: '#f9fafb' },
                                '& td': { py: 0.7, borderBottom: '1px solid #f3f4f6', fontSize: 13 },
                              }}>
                              <TableCell sx={{ pl: 2 }}>
                                <Typography sx={{ fontWeight: 700, color: row.severity_tier >= 2 ? s.text : '#1e293b', fontSize: 13 }}>
                                  {row.ticker}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={sevLabel(row.severity_tier)} size="small"
                                  sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: s.bg, color: '#fff',
                                    '& .MuiChip-label': { px: 0.5 } }} />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: s.bg, minWidth: 38 }}>
                                    {(row.ml_prob * 100).toFixed(1)}%
                                  </Typography>
                                  <Box sx={{ flex: 1, maxWidth: 80, height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: '#f3f4f6' }}>
                                    <Box sx={{ height: '100%', borderRadius: 2,
                                      width: `${Math.min(row.ml_prob * 100, 100)}%`, bgcolor: s.bg }} />
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Typography sx={{
                                  fontWeight: 700, fontSize: 13,
                                  color: row.target_position < 0.3 ? '#ef4444' : row.target_position < 0.7 ? '#ea580c' : '#22c55e',
                                }}>
                                  {(row.target_position * 100).toFixed(0)}%
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: confClr }}>{conf}</Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                {row.dynamic_alert && (
                                  <Chip size="small" label={t('riskML.signals.alert')} sx={{
                                    height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#ef4444', color: '#fff',
                                    '& .MuiChip-label': { px: 0.5 } }} />
                                )}
                                {row.re_entry_signal && !row.dynamic_alert && (
                                  <Chip size="small" label={t('riskML.signals.reEntry')} sx={{
                                    height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#22c55e', color: '#fff',
                                    '& .MuiChip-label': { px: 0.5 } }} />
                                )}
                              </TableCell>
                              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: actionClr(ak) }}>
                                  {t(`riskML.actions.${ak}`)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Expand safe */}
                        {g.tier === 0 && !safeExpanded && g.items.length > 25 && (
                          <TableRow sx={{ '& td': { border: 0 } }}>
                            <TableCell colSpan={7} align="center" sx={{ py: 0.8 }}>
                              <Button size="small" onClick={() => setSafeExpanded(true)}
                                endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, color: '#6b7280' }}>
                                {t('riskML.heatmap.moreSafeStocks', { count: g.items.length - 25 })}
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                        {g.tier === 0 && safeExpanded && (
                          <TableRow sx={{ '& td': { border: 0 } }}>
                            <TableCell colSpan={7} align="center" sx={{ py: 0.8 }}>
                              <Button size="small" onClick={() => setSafeExpanded(false)}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, color: '#6b7280' }}>
                                {t('riskML.collapse')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Section>

        {/* ═══════════════════════════════════════════════════════
            RE-ENTRY TABLE
            ═══════════════════════════════════════════════════════ */}
        {reEntries.length > 0 && (
          <Section
            title={t('riskML.sections.reEntryTitle', { count: reEntries.length })}
            icon={<TrendUpIcon sx={{ fontSize: 16, color: '#22c55e' }} />}
            contentSx={{ p: 0 }}
            tooltip={t('riskML.sections.reEntryDesc')}
          >
            {/* Explanation banner */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
              <Alert severity="success" variant="outlined" icon={<TrendUpIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: 12 } }}>
                {t('riskML.reEntry.explanation')}
              </Alert>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)',
                    '& th': { fontWeight: 700, fontSize: 11, color: '#15803d', py: 1,
                      borderBottom: '2px solid #bbf7d0', whiteSpace: 'nowrap', bgcolor: '#f0fdf4' },
                  }}>
                    <TableCell>{t('riskML.tableHeaders.ticker')}</TableCell>
                    <TableCell align="center">{t('riskML.tableHeaders.riskProb')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('riskML.reEntry.smoothedTooltip')} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
                          {t('riskML.reEntry.smoothedRisk')} <InfoIcon sx={{ fontSize: 11, color: '#86efac' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('riskML.reEntry.recoveryTooltip')} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
                          {t('riskML.reEntry.recoveryScore')} <InfoIcon sx={{ fontSize: 11, color: '#86efac' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">{t('riskML.tableHeaders.confidence')}</TableCell>
                    <TableCell align="center">{t('riskML.tableHeaders.positionSize')}</TableCell>
                    <TableCell>{t('riskML.reEntry.whyBuy')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reEntries.map(s => {
                    const unc = (s.ensemble_std || 0) * 100;
                    const conf = unc < 3 ? t('riskML.confidenceLevels.high') : unc < 8 ? t('riskML.confidenceLevels.med') : t('riskML.confidenceLevels.low');
                    const confClr = unc < 3 ? '#22c55e' : unc < 8 ? '#ea580c' : '#ef4444';
                    const recScore = s.recovery_score || 0;
                    const recLabel = recScore >= 3 ? t('riskML.reEntry.recoveryStrong') : recScore >= 2 ? t('riskML.reEntry.recoveryModerate') : t('riskML.reEntry.recoveryEarly');
                    const recClr = recScore >= 3 ? '#22c55e' : recScore >= 2 ? '#eab308' : '#9ca3af';
                    // Build reason chips
                    const reasons = [];
                    if (s.ml_prob < 0.05) reasons.push(t('riskML.reEntry.reasonVeryLowRisk'));
                    else if (s.ml_prob < 0.15) reasons.push(t('riskML.reEntry.reasonLowRisk'));
                    if (recScore >= 3) reasons.push(t('riskML.reEntry.reasonStrongRecovery'));
                    else if (recScore >= 2) reasons.push(t('riskML.reEntry.reasonRecovering'));
                    if (unc < 3) reasons.push(t('riskML.reEntry.reasonHighConfidence'));
                    if (s.target_position >= 0.9) reasons.push(t('riskML.reEntry.reasonFullPosition'));
                    return (
                      <TableRow key={s.ticker} hover onClick={() => navigate(`/risk-detail/${s.ticker}`)}
                        sx={{
                          cursor: 'pointer',
                          '& td:first-of-type': { borderLeft: '3px solid #22c55e' },
                          background: 'linear-gradient(90deg, #f0fdf4 0%, #fff 30%)',
                          '&:hover': { bgcolor: '#ecfdf5' },
                          '& td': { borderBottom: '1px solid #dcfce7', py: 1.2 },
                        }}>
                        <TableCell>
                          <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#15803d' }}>{s.ticker}</Typography>
                            <Chip label={t('riskML.signals.reEntry')} size="small" sx={{
                              height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#22c55e', color: '#fff',
                              mt: 0.3, '& .MuiChip-label': { px: 0.5 },
                            }} />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#22c55e' }}>
                            {(s.ml_prob*100).toFixed(1)}%
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: '#86efac' }}>
                            {t('riskML.reEntry.veryLow')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#16a34a' }}>
                            {((s.smoothed_prob||0)*100).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: '2px' }}>
                              {[1,2,3].map(i => (
                                <Box key={i} sx={{
                                  width: 10, height: 10, borderRadius: '50%',
                                  bgcolor: i <= recScore ? recClr : '#e5e7eb',
                                  transition: 'all 0.2s',
                                }} />
                              ))}
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: recClr }}>
                              {recLabel}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: confClr }}>{conf}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#22c55e' }}>
                            {(s.target_position*100).toFixed(0)}%
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: '#86efac' }}>
                            {t('riskML.reEntry.maxAllocation')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
                            {reasons.map((r, i) => (
                              <Chip key={i} label={r} size="small" sx={{
                                height: 18, fontSize: 10, fontWeight: 600,
                                bgcolor: '#dcfce7', color: '#15803d',
                                border: '1px solid #bbf7d0',
                                '& .MuiChip-label': { px: 0.6 },
                              }} />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        )}
      </Box>

      {/* ═══ SNACKBAR ═══ */}
      <Snackbar open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled"
          sx={{ width: '100%', borderRadius: 1.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskDashboard;
